import { Box, Circle, HStack, Skeleton, VStack, useColorMode } from '@chakra-ui/react';
import { CONTENT_HEADER_HEIGHT, TOP_MENU_HEIGHT } from 'Molecules';
import { BREADCRUMBS_HEIGHT } from 'Molecules/Breadcrumbs';
import { Typography, colors } from 'Tokens';
import { RefObject, useMemo } from 'react';
import { PieChart } from 'react-minimal-pie-chart';
import { ReportStructure } from '../ReportComponents/ReportStructure';
import { StructureNode } from '../../Assessment/Structure';
import { useUserCompanyCheck } from 'utils/hooks';
import { useParams } from 'react-router-dom';
import { useEsrsAssessmentQuery } from 'models';
import { SubsidiaryWarning } from '../../SubsidiaryWarning';

const CompletionProgressBox = ({ progress }: { progress: string }) => {
  const { colorMode } = useColorMode();
  const isDarkMode = useMemo(() => colorMode === 'dark', [colorMode]);
  const color = useMemo(() => (isDarkMode ? '_dark' : 'default'), [isDarkMode]);

  return (
    <HStack
      gap="16px"
      bgColor="bg.info"
      border="1px solid"
      borderRadius="8px"
      borderColor="bg.info"
      padding="16px"
    >
      <Circle size="32px" alignItems="center">
        <PieChart
          lineWidth={16}
          data={[
            {
              title: 'Collected',
              value: Number(progress),
              color: colors['bg.progress'][color],
            },
            {
              title: 'Missing',
              value: 100 - Number(progress),
              color: colors['bg.unknown'][color],
            },
          ]}
        />
      </Circle>
      <VStack alignItems="stretch" gap="2px">
        <Typography variant="bodyStrong">Completion progress</Typography>
        <Typography variant="detail">{progress}%</Typography>
      </VStack>
    </HStack>
  );
};

type ReportSideMenuProps = {
  structure: StructureNode[][][];
  selectedStandard: string;
  selectedDr: string;
  isLoading: boolean;
  reportRef: RefObject<HTMLDivElement | null>;
  setSelectedDr: (val: string) => void;
  setSelectedStandard: (val: string) => void;
  selectedStandardDRs: StructureNode[];
  progress: string;
};

const ReportSideMenu = ({
  structure,
  selectedStandard,
  selectedDr,
  isLoading,
  reportRef,
  setSelectedDr,
  setSelectedStandard,
  selectedStandardDRs,
  progress,
}: ReportSideMenuProps) => {
  const { esrsAssessmentId = '' } = useParams();
  const { hasCompany: checkSubsidiariesAccess, loading } = useUserCompanyCheck();

  const { data } = useEsrsAssessmentQuery({
    variables: { esrsAssessmentId },
    skip: !esrsAssessmentId,
  });
  const hasAccessToAllSubs = data?.esrsAssessment?.subsidiaryAssessments.every((sub) =>
    checkSubsidiariesAccess(sub.company.id)
  );

  return (
    <Skeleton isLoaded={!isLoading && !loading}>
      <VStack
        alignItems="stretch"
        minW="242px"
        maxW="242px"
        gap="16px"
        borderBottomColor="border.decorative"
        overflowX="hidden"
        marginTop="16px"
        height={`calc(100vh - ${TOP_MENU_HEIGHT} - ${BREADCRUMBS_HEIGHT} - ${CONTENT_HEADER_HEIGHT})`}
        position="sticky"
        top="56px"
      >
        {<CompletionProgressBox progress={progress} />}
        {!hasAccessToAllSubs && <SubsidiaryWarning isWordReport />}
        <Typography variant="overline">CONTENT</Typography>
        <Box
          overflowX="hidden"
          overflowY="scroll"
          css={{
            '&::-webkit-scrollbar': {
              width: '0',
            },
            scrollbarWidth: 'none',
          }}
        >
          <ReportStructure
            nodes={structure}
            selectedDr={selectedDr}
            reportRef={reportRef}
            selectedStandard={selectedStandard}
            setSelectedDr={setSelectedDr}
            setSelectedStandard={setSelectedStandard}
            selectedStandardDRs={selectedStandardDRs}
          />
        </Box>
      </VStack>
    </Skeleton>
  );
};

export default ReportSideMenu;
